import { PERMISSIONS } from '../context/AuthContext';

/**
 * Route permission mapping
 * Maps each route to the permissions required to access it
 * Updated with comprehensive 93 permissions system
 */
export const ROUTE_PERMISSIONS = {
  // Public routes - accessible without authentication
  '/': {
    permissions: [], // No permissions required
    isPublic: true
  },
  '/Login': {
    permissions: [], // No permissions required
    isPublic: true
  },
  '/customer-login': {
    permissions: [],
    isPublic: true // Customer self-login — no pre-auth permission required
  },
  '/ServerError': {
    permissions: [], // No permissions required
    isPublic: true
  },

  // Home and Dashboard - accessible by all authenticated users
  '/home': {
    permissions: [] // Accessible by any authenticated user
  },
  '/customer-home': {
    permissions: [] // Authenticated customer home
  },
  '/customer-shipments': {
    permissions: [] // A customer can always view their own shipments
  },
  '/customer-shipment/:shipmentId/tracking': {
    permissions: [], // Public tracking page for a shipment
    isPublic: true
  },
  '/dashboard': {
    permissions: [PERMISSIONS.DASHBOARD_VIEW_ANALYTICS] // Requires dashboard analytics permission
  },

  // Shipment Management
  '/shipments': {
    permissions: [PERMISSIONS.SHIPMENTS_LIST] // Requires shipments list permission
  },
  '/Shipments': {
    permissions: [PERMISSIONS.SHIPMENTS_LIST] // Requires shipments list permission
  },
  '/shipment/AddShipment': {
    permissions: [PERMISSIONS.SHIPMENTS_CREATE] // Requires shipments create permission
  },
  '/Shipment/AddShipment': {
    permissions: [PERMISSIONS.SHIPMENTS_CREATE] // Requires shipments create permission
  },
  '/shipment/:id/EditShipment': {
    permissions: [PERMISSIONS.SHIPMENTS_UPDATE] // Requires shipments update permission
  },
  '/Shipment/:id/EditShipment': {
    permissions: [PERMISSIONS.SHIPMENTS_UPDATE] // Requires shipments update permission
  },
  '/shipment/import': {
    permissions: [PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL] // Requires shipments upload excel permission
  },
  '/Shipment/Import': {
    permissions: [PERMISSIONS.SHIPMENTS_UPLOAD_EXCEL] // Requires shipments upload excel permission
  },
  '/shipment/scan': {
    permissions: [PERMISSIONS.SHIPMENTS_SCAN_UPDATE] // Scan / receive / pay on scan page
  },
  '/Shipment/Scan': {
    permissions: [PERMISSIONS.SHIPMENTS_SCAN_UPDATE] // Scan / receive / pay on scan page
  },
  '/shipment/PendingShipmentForm': {
    permissions: [PERMISSIONS.SHIPMENTS_CREATE] // Requires shipments create permission
  },
  '/Shipment/PendingShipmentForm': {
    permissions: [PERMISSIONS.SHIPMENTS_CREATE] // Requires shipments create permission
  },

  // Pricing rules (unified pricing)
  '/PricingRules': {
    permissions: [PERMISSIONS.PRICING_RULES_LIST],
  },
  '/PricingRules/Add': {
    permissions: [PERMISSIONS.PRICING_RULES_CREATE],
  },
  '/PricingRules/:id/Edit': {
    permissions: [PERMISSIONS.PRICING_RULES_UPDATE],
  },
  '/PricingRules/Import': {
    permissions: [PERMISSIONS.PRICING_RULES_IMPORT],
  },
  '/PricingPlans': {
    permissions: [PERMISSIONS.PRICING_PLANS_LIST],
  },
  '/PricingPlans/Add': {
    permissions: [PERMISSIONS.PRICING_PLANS_CREATE],
  },
  '/PricingPlans/:id': {
    permissions: [PERMISSIONS.PRICING_PLANS_VIEW, PERMISSIONS.PRICING_PLANS_UPDATE],
  },
  // Legacy /Services URLs redirect to /PricingRules; entries kept for direct hits if redirect not used
  '/services': {
    permissions: [PERMISSIONS.PRICING_RULES_LIST],
  },
  '/Services': {
    permissions: [PERMISSIONS.PRICING_RULES_LIST],
  },
  '/services/AddService': {
    permissions: [PERMISSIONS.PRICING_RULES_CREATE],
  },
  '/Services/AddService': {
    permissions: [PERMISSIONS.PRICING_RULES_CREATE],
  },
  '/services/:id/EditService': {
    permissions: [PERMISSIONS.PRICING_RULES_UPDATE],
  },
  '/Services/:id/EditService': {
    permissions: [PERMISSIONS.PRICING_RULES_UPDATE],
  },

  // Deprecated standalone /pricing routes — map to unified rules
  '/pricing': {
    permissions: [PERMISSIONS.PRICING_RULES_LIST],
  },
  '/pricing/AddPricing': {
    permissions: [PERMISSIONS.PRICING_RULES_CREATE],
  },
  '/pricing/:id/EditPricing': {
    permissions: [PERMISSIONS.PRICING_RULES_UPDATE],
  },

  // Invoice Management
  '/invoices': {
    permissions: [PERMISSIONS.INVOICES_LIST] // Requires invoices list permission
  },
  '/Invoices': {
    permissions: [PERMISSIONS.INVOICES_LIST] // Requires invoices list permission
  },
  '/invoices/AddInvoice': {
    permissions: [PERMISSIONS.INVOICES_CREATE] // Requires invoices create permission
  },
  '/Invoices/AddInvoice': {
    permissions: [PERMISSIONS.INVOICES_CREATE] // Requires invoices create permission
  },
  '/invoices/:id/EditInvoice': {
    permissions: [PERMISSIONS.INVOICES_UPDATE] // Requires invoices update permission
  },
  '/Invoices/:id/EditInvoice': {
    permissions: [PERMISSIONS.INVOICES_UPDATE] // Requires invoices update permission
  },

  // Customer Management
  '/customers': {
    permissions: [PERMISSIONS.CUSTOMERS_LIST] // Requires customers list permission
  },
  '/Customers': {
    permissions: [PERMISSIONS.CUSTOMERS_LIST] // Requires customers list permission
  },
  '/customers/AddCustomer': {
    permissions: [PERMISSIONS.CUSTOMERS_CREATE] // Requires customers create permission
  },
  '/Customers/AddCustomer': {
    permissions: [PERMISSIONS.CUSTOMERS_CREATE] // Requires customers create permission
  },
  '/customers/:id/EditCustomer': {
    permissions: [PERMISSIONS.CUSTOMERS_UPDATE] // Requires customers update permission
  },
  '/Customers/:id/EditCustomer': {
    permissions: [PERMISSIONS.CUSTOMERS_UPDATE] // Requires customers update permission
  },
  '/customers/pending-registrations': {
    permissions: [PERMISSIONS.CUSTOMERS_APPROVE]
  },

  // WhatsApp
  '/SendMessage': {
    permissions: [PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION] // Requires whatsapp send shipment notification permission
  },
  '/whatsapp': {
    permissions: [PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION] // Requires whatsapp send shipment notification permission
  },

  // Settings Management
  '/settings': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_PUBLIC] // Requires settings view public permission
  },
  '/settings/files': {
    permissions: [PERMISSIONS.SETTINGS_FILES_LIST] // Requires settings files list permission
  },
  '/settings/upload': {
    permissions: [PERMISSIONS.SETTINGS_FILES_UPLOAD] // Requires settings files upload permission
  },
  '/settings/general': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_PUBLIC]
  },
  '/settings/payment': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_DETAIL]
  },
  '/settings/twilio': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_DETAIL]
  },
  '/settings/whatsapp-messages': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_DETAIL]
  },
  '/settings/whatsapp-chatbot': {
    permissions: [PERMISSIONS.SETTINGS_VIEW_DETAIL]
  },
  '/settings/shipments-config': {
    permissions: [PERMISSIONS.SETTINGS_LIST]
  },
  '/settings/permissions': {
    permissions: [PERMISSIONS.PERMISSIONS_LIST]
  },
  '/settings/roles': {
    permissions: [PERMISSIONS.ROLES_LIST]
  },
  '/settings/users': {
    permissions: [PERMISSIONS.USERS_LIST]
  },
  '/settings/user-roles': {
    permissions: [PERMISSIONS.USER_ROLES_LIST]
  },
  '/settings/shipment-types': {
    permissions: [PERMISSIONS.SHIPMENT_TYPES_LIST]
  },
  '/settings/locations': {
    permissions: [PERMISSIONS.LOCATIONS_LIST]
  },
  '/settings/accounts': {
    permissions: [PERMISSIONS.ACCOUNTS_LIST]
  },
  '/settings/settlementgroups': {
    permissions: [PERMISSIONS.SETTLEMENT_GROUPS_LIST],
  },
  '/settings/settlementgroups/Add': {
    permissions: [PERMISSIONS.SETTLEMENT_GROUPS_CREATE],
  },
  '/settings/settlementgroups/:id': {
    permissions: [PERMISSIONS.SETTLEMENT_GROUPS_VIEW, PERMISSIONS.SETTLEMENT_GROUPS_UPDATE],
  },

  // Payment Management
  '/payments': {
    permissions: [PERMISSIONS.PAYMENTS_LIST]
  },
  '/payments/shipment': {
    permissions: [PERMISSIONS.PAYMENTS_ADD_SHIPMENT] // Requires payments add shipment permission
  },
  '/payments/invoice': {
    permissions: [PERMISSIONS.PAYMENTS_ADD_INVOICE] // Requires payments add invoice permission
  },

  // User Management (Admin only)
  '/users': {
    permissions: [PERMISSIONS.USERS_LIST] // Requires users list permission
  },
  '/users/AddUser': {
    permissions: [PERMISSIONS.USERS_CREATE] // Requires users create permission
  },
  '/users/:id/EditUser': {
    permissions: [PERMISSIONS.USERS_UPDATE] // Requires users update permission
  },

  // Role Management (Admin only)
  '/roles': {
    permissions: [PERMISSIONS.ROLES_LIST] // Requires roles list permission
  },
  '/roles/AddRole': {
    permissions: [PERMISSIONS.ROLES_CREATE] // Requires roles create permission
  },
  '/roles/:id/EditRole': {
    permissions: [PERMISSIONS.ROLES_UPDATE] // Requires roles update permission
  },

  // Location Management
  '/locations': {
    permissions: [PERMISSIONS.LOCATIONS_MANAGE] // Requires locations manage permission
  },
  '/shipment-types': {
    permissions: [PERMISSIONS.SHIPMENT_TYPES_LIST]
  },
  '/shipment/receiver-details': {
    permissions: [PERMISSIONS.RECEIVER_DETAILS_LIST] // Requires receiver details list permission
  },
  '/shipment/tracking-events': {
    permissions: [PERMISSIONS.TRACKING_EVENTS_LIST]
  },
  '/shipment/:shipmentId/tracking': {
    permissions: [PERMISSIONS.TRACKING_EVENTS_LIST]
  },
  '/shipment/tracking-events/add': {
    permissions: [PERMISSIONS.TRACKING_EVENTS_CREATE]
  },
  '/shipment/tracking-events/:id/edit': {
    permissions: [PERMISSIONS.TRACKING_EVENTS_UPDATE]
  },
  '/settings/tracking-event-types': {
    permissions: [PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST]
  },
  '/settings/shipping-companies': {
    permissions: [PERMISSIONS.SHIPPING_COMPANIES_LIST]
  },
  '/settings/shipping-warehouses': {
    permissions: [PERMISSIONS.SHIPPING_WAREHOUSES_LIST]
  },
  '/shipment/units': {
    permissions: [PERMISSIONS.SHIPMENT_UNITS_LIST]
  },
  '/shipment/unit-tracking-events': {
    permissions: [PERMISSIONS.SHIPMENT_UNIT_TRACKING_EVENTS_LIST]
  },
  '/shipment/pieces': {
    permissions: [PERMISSIONS.SHIPMENT_PIECES_LIST]
  },
  '/shipment/pieces/scan': {
    permissions: [PERMISSIONS.SHIPMENT_PIECES_SCAN_UPDATE]
  },
  '/shipment/piece-tracking-events': {
    permissions: [PERMISSIONS.SHIPMENT_PIECE_TRACKING_EVENTS_LIST]
  },
  '/scan': {
    permissions: [PERMISSIONS.SCAN_RESOLVE]
  },
  '/track': {
    permissions: [],
    isPublic: true
  },
  '/track/:barcode': {
    permissions: [],
    isPublic: true
  },
  '/pay/result': {
    permissions: [],
    isPublic: true
  },
  '/current-location': {
    permissions: [PERMISSIONS.CURRENT_LOCATION_MANAGE] // Requires current location manage permission
  },

};

/**
 * Check if a user with given permissions can access a specific route
 * @param {string[]} userPermissions - Array of user permissions
 * @param {string} routePath - The route path to check
 * @returns {boolean} - True if user can access the route
 */
export const canAccessRoute = (userPermissions, routePath) => {
  // First try exact match
  let routeConfig = ROUTE_PERMISSIONS[routePath];
  
  // If no exact match, try to find a matching pattern for dynamic routes
  if (!routeConfig) {
    for (const [pattern, config] of Object.entries(ROUTE_PERMISSIONS)) {
      if (pattern.includes(':')) {
        // Convert pattern to regex (replace :param with [^/]+)
        const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${regexPattern}$`);
        if (regex.test(routePath)) {
          routeConfig = config;
          break;
        }
      }
    }
  }
  
  if (!routeConfig) return true; // Allow access by default for routes not in mapping (backward compatibility)
  
  // Public routes are accessible to everyone
  if (routeConfig.isPublic) return true;

  // If the route does not require any specific permissions, allow access
  // to any authenticated user (ProtectedRoute already verified authentication).
  if (!routeConfig.permissions || routeConfig.permissions.length === 0) {
    return true;
  }

  // For routes that do require permissions, the user must have some permissions
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  // Check if user has all required permissions for the route
  return routeConfig.permissions.every(permission => userPermissions.includes(permission));
};

/**
 * Get the required permissions for a route
 * @param {string} routePath - The route path
 * @returns {string[]} - Array of required permissions
 */
export const getRoutePermissions = (routePath) => {
  const routeConfig = ROUTE_PERMISSIONS[routePath];
  return routeConfig ? routeConfig.permissions || [] : [];
};

/**
 * Get all routes that a user can access
 * @param {string[]} userPermissions - Array of user permissions
 * @returns {string[]} - Array of accessible route paths
 */
export const getAccessibleRoutes = (userPermissions) => {
  if (!userPermissions || userPermissions.length === 0) return [];
  
  return Object.keys(ROUTE_PERMISSIONS).filter(route => 
    canAccessRoute(userPermissions, route)
  );
};
